import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { usePlayer } from '../contexts/PlayerContext';

/**
 * FullPlayerModal — P1/P2 PWA mobile chrome.
 *
 * P2 lift: subscribes to the global PlayerContext for LIVE playback state
 * (currentTime, duration, isPlaying) so the scrubber, transport, and chapter
 * list work from ANY route — not just /player. The audio element is owned
 * persistently by PlayerProvider so navigating away no longer kills playback.
 *
 * Features:
 *   • Live scrubber — tap or drag to seek; live `m:ss / m:ss` readout.
 *   • Sleep timer bottom sheet + countdown chip + auto-pause.
 *   • Chapter ⏮/⏭ transport buttons + dedicated "Chapters" sheet (full list
 *     with progress dots + scroll-to-current).
 *   • Hold-to-fast-forward on ±15s buttons.
 *   • Drag-down 120px to dismiss.
 */
const dispatchCommand = (action, payload = {}) => {
  try {
    window.dispatchEvent(new CustomEvent('vs-player-command', { detail: { action, ...payload } }));
  } catch (_) { /* noop */ }
};

const hapt = (ms = 15) => {
  if (window.navigator && typeof window.navigator.vibrate === 'function') {
    try { window.navigator.vibrate(ms); } catch (_) { /* noop */ }
  }
};

const HOLD_THRESHOLD_MS = 300;
const FAST_RATE = 2.0;
const SLEEP_OPTIONS = [
  { id: 'off', label: 'Off', minutes: 0 },
  { id: '5', label: '5 min', minutes: 5 },
  { id: '15', label: '15 min', minutes: 15 },
  { id: '30', label: '30 min', minutes: 30 },
  { id: '60', label: '1 hour', minutes: 60 },
];

const formatTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.floor(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const formatMs = (ms) => {
  if (!ms || ms <= 0) return '';
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const FullPlayerModal = ({ nowPlaying, onClose }) => {
  const navigate = useNavigate();
  const player = usePlayer();
  // Prefer richer info from PlayerContext but accept prop fallback (legacy).
  const np = player.nowPlaying && player.nowPlaying.bookId ? player.nowPlaying : nowPlaying;
  const chapters = Array.isArray(np?.chapters) ? np.chapters : [];

  const [playbackRate, setPlaybackRate] = useState(1);
  const [sleepEndAt, setSleepEndAt] = useState(0);
  const [sleepRemainingMs, setSleepRemainingMs] = useState(0);
  const [sleepSheetOpen, setSleepSheetOpen] = useState(false);
  const [chaptersSheetOpen, setChaptersSheetOpen] = useState(false);
  const [scrubFraction, setScrubFraction] = useState(null); // null when not dragging

  // Hold-to-fast-forward bookkeeping
  const holdTimerRef = useRef(null);
  const holdActiveRef = useRef(false);
  const prevRateRef = useRef(1);

  // Listen for legacy `vs-player-state` events for rate / sleep timer (still
  // dispatched by EnhancedPlayerPage). Playback state itself now lives in the
  // PlayerProvider context.
  useEffect(() => {
    const handler = (e) => {
      const detail = e?.detail || {};
      if (detail.playbackRate !== undefined) setPlaybackRate(Number(detail.playbackRate) || 1);
      if (detail.sleepTimerEndAt !== undefined) setSleepEndAt(detail.sleepTimerEndAt || 0);
    };
    window.addEventListener('vs-player-state', handler);
    dispatchCommand('state-request');
    return () => window.removeEventListener('vs-player-state', handler);
  }, []);

  // Tick the sleep timer countdown.
  useEffect(() => {
    if (!sleepEndAt) { setSleepRemainingMs(0); return undefined; }
    const tick = () => {
      const remain = sleepEndAt - Date.now();
      if (remain <= 0) { setSleepRemainingMs(0); setSleepEndAt(0); }
      else setSleepRemainingMs(remain);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [sleepEndAt]);

  // ── Transport actions ───────────────────────────────────────────────
  const togglePlay = () => {
    hapt(20);
    if (player.togglePlay) player.togglePlay();
    else dispatchCommand(player.isPlaying ? 'pause' : 'play');
  };

  const seek = (delta) => {
    hapt(10);
    if (player.seekRelative) player.seekRelative(delta);
    else dispatchCommand('seek-relative', { delta });
  };

  const openFullPlayer = () => {
    hapt(20);
    onClose();
    navigate(`/player?bookId=${encodeURIComponent(np?.bookId || '')}`);
  };

  const goChapter = (dir) => {
    hapt(18);
    dispatchCommand(dir === 'next' ? 'chapter-next' : 'chapter-prev');
  };

  const jumpToChapter = (chapter) => {
    hapt(18);
    dispatchCommand('chapter-jump', { chapterId: chapter.id, chapterNumber: chapter.chapter_number });
    setChaptersSheetOpen(false);
  };

  // ── Live scrubber ───────────────────────────────────────────────────
  const duration = player.duration || 0;
  const currentTime = player.currentTime || 0;
  const displayFraction = scrubFraction !== null
    ? scrubFraction
    : (duration > 0 ? Math.min(1, Math.max(0, currentTime / duration)) : 0);
  const displayCurrent = scrubFraction !== null && duration > 0 ? scrubFraction * duration : currentTime;

  const scrubberRef = useRef(null);
  const pointerDown = (e) => {
    if (!duration || !scrubberRef.current) return;
    e.preventDefault();
    const rect = scrubberRef.current.getBoundingClientRect();
    const x = ((e.touches ? e.touches[0].clientX : e.clientX) - rect.left) / rect.width;
    setScrubFraction(Math.min(1, Math.max(0, x)));
  };
  const pointerMove = (e) => {
    if (scrubFraction === null || !scrubberRef.current) return;
    const rect = scrubberRef.current.getBoundingClientRect();
    const x = ((e.touches ? e.touches[0].clientX : e.clientX) - rect.left) / rect.width;
    setScrubFraction(Math.min(1, Math.max(0, x)));
  };
  const pointerUp = () => {
    if (scrubFraction === null) return;
    const target = scrubFraction * duration;
    setScrubFraction(null);
    hapt(10);
    if (player.seekTo) player.seekTo(target);
    else dispatchCommand('seek-absolute', { time: target });
  };
  useEffect(() => {
    if (scrubFraction === null) return undefined;
    window.addEventListener('pointermove', pointerMove);
    window.addEventListener('pointerup', pointerUp);
    window.addEventListener('touchmove', pointerMove, { passive: false });
    window.addEventListener('touchend', pointerUp);
    return () => {
      window.removeEventListener('pointermove', pointerMove);
      window.removeEventListener('pointerup', pointerUp);
      window.removeEventListener('touchmove', pointerMove);
      window.removeEventListener('touchend', pointerUp);
    };
  }, [scrubFraction]);

  // ── Hold-to-fast-forward ────────────────────────────────────────────
  const beginHold = () => {
    if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
    holdTimerRef.current = setTimeout(() => {
      holdActiveRef.current = true;
      prevRateRef.current = playbackRate || 1;
      hapt(30);
      if (player.setRate) player.setRate(FAST_RATE);
      dispatchCommand('rate', { value: FAST_RATE });
    }, HOLD_THRESHOLD_MS);
  };
  const endHold = (delta) => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    if (holdActiveRef.current) {
      holdActiveRef.current = false;
      const restore = prevRateRef.current || 1;
      if (player.setRate) player.setRate(restore);
      dispatchCommand('rate', { value: restore });
    } else {
      seek(delta);
    }
  };
  const cancelHold = () => {
    if (holdTimerRef.current) { clearTimeout(holdTimerRef.current); holdTimerRef.current = null; }
    if (holdActiveRef.current) {
      holdActiveRef.current = false;
      const restore = prevRateRef.current || 1;
      if (player.setRate) player.setRate(restore);
      dispatchCommand('rate', { value: restore });
    }
  };

  // ── Sleep timer ─────────────────────────────────────────────────────
  const setSleep = (minutes) => {
    hapt(20);
    setSleepSheetOpen(false);
    if (minutes <= 0) dispatchCommand('sleep-timer-cancel');
    else dispatchCommand('sleep-timer-set', { minutes });
  };

  const handleDragEnd = (_, info) => {
    if (info.offset.y > 120 || info.velocity.y > 600) {
      hapt(25);
      onClose();
    }
  };

  // Compute current chapter index from nowPlaying for the position chip.
  const currentChapterIndex = chapters.findIndex((c) => c.chapter_number === np?.chapterNumber);

  return (
    <>
      <motion.div
        className="vs-fp-backdrop"
        data-testid="full-player-backdrop"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        onClick={onClose}
      />

      <motion.div
        layoutId="vs-player-card"
        className="vs-fp-sheet"
        data-testid="full-player-modal"
        drag="y"
        dragConstraints={{ top: 0, bottom: 0 }}
        dragElastic={0.25}
        onDragEnd={handleDragEnd}
        transition={{ type: 'spring', damping: 28, stiffness: 320 }}
      >
        <div className="vs-fp-handle" aria-hidden="true" />

        <div className="vs-fp-topbar">
          <button type="button" className="vs-fp-icon-btn" data-testid="full-player-close-button" aria-label="Close player" onClick={onClose}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
              <path d="m6 9 6 6 6-6" />
            </svg>
          </button>
          <div className="vs-fp-topbar-title">Now Playing</div>
          <div className="vs-fp-topbar-actions">
            {chapters.length > 1 && (
              <button
                type="button"
                className="vs-fp-icon-btn"
                data-testid="full-player-chapters-toggle"
                aria-label="Chapters"
                onClick={() => { hapt(15); setChaptersSheetOpen(true); }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="8" y1="6" x2="21" y2="6" />
                  <line x1="8" y1="12" x2="21" y2="12" />
                  <line x1="8" y1="18" x2="21" y2="18" />
                  <line x1="3" y1="6" x2="3.01" y2="6" />
                  <line x1="3" y1="12" x2="3.01" y2="12" />
                  <line x1="3" y1="18" x2="3.01" y2="18" />
                </svg>
              </button>
            )}
            <button
              type="button"
              className={`vs-fp-icon-btn ${sleepEndAt ? 'vs-fp-icon-active' : ''}`}
              data-testid="full-player-sleep-toggle"
              aria-label="Sleep timer"
              onClick={() => { hapt(15); setSleepSheetOpen((o) => !o); }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
              </svg>
            </button>
          </div>
        </div>

        {sleepEndAt > 0 && sleepRemainingMs > 0 && (
          <div className="vs-fp-sleep-chip" data-testid="full-player-sleep-chip">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
            </svg>
            <span>Sleep in {formatMs(sleepRemainingMs)}</span>
            <button type="button" className="vs-fp-sleep-chip-x" data-testid="full-player-sleep-cancel" aria-label="Cancel sleep timer" onClick={() => setSleep(0)}>×</button>
          </div>
        )}

        <motion.div layoutId="vs-player-cover" className="vs-fp-cover" transition={{ type: 'spring', damping: 28, stiffness: 320 }}>
          {np?.cover ? (
            <img src={np.cover} alt={np.title || 'Book cover'} />
          ) : (
            <div className="vs-fp-cover-fallback" aria-hidden="true">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.25">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
              </svg>
            </div>
          )}
        </motion.div>

        <div className="vs-fp-meta">
          <h2 className="vs-fp-title" data-testid="full-player-title">{np?.title || 'Resume listening'}</h2>
          <p className="vs-fp-sub" data-testid="full-player-chapter">
            {np?.chapter || 'Chapter 1'}
            {chapters.length > 0 && currentChapterIndex >= 0 && (
              <span className="vs-fp-chapter-pos"> · {currentChapterIndex + 1}/{chapters.length}</span>
            )}
            {playbackRate !== 1 && (
              <span className="vs-fp-rate-chip" data-testid="full-player-rate-chip"> · {playbackRate}×</span>
            )}
          </p>
        </div>

        {/* Live scrubber */}
        <div className="vs-fp-scrubber" data-testid="full-player-scrubber">
          <div
            ref={scrubberRef}
            className={`vs-fp-scrubber-track ${duration > 0 ? 'is-active' : ''}`}
            onPointerDown={duration > 0 ? pointerDown : undefined}
            onTouchStart={duration > 0 ? pointerDown : undefined}
            role="slider"
            aria-label="Playback position"
            aria-valuemin={0}
            aria-valuemax={duration || 100}
            aria-valuenow={displayCurrent || 0}
          >
            <div className="vs-fp-scrubber-fill" style={{ width: `${displayFraction * 100}%` }} />
            <div className="vs-fp-scrubber-thumb" style={{ left: `${displayFraction * 100}%` }} />
          </div>
          <div className="vs-fp-scrubber-times">
            <span data-testid="full-player-current-time">{duration > 0 ? formatTime(displayCurrent) : '—:—'}</span>
            <span data-testid="full-player-duration">{duration > 0 ? formatTime(duration) : 'Open full player'}</span>
          </div>
        </div>

        <div className="vs-fp-controls">
          <button
            type="button"
            className="vs-fp-chapter-btn"
            data-testid="full-player-chapter-prev"
            aria-label="Previous chapter"
            disabled={chapters.length === 0 || currentChapterIndex <= 0}
            onClick={() => goChapter('prev')}
          >
            <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 5v14" /><path d="M20 5 9 12l11 7z" />
            </svg>
          </button>

          <button
            type="button"
            className="vs-fp-skip-btn"
            data-testid="full-player-back-15"
            aria-label="Back 15 seconds (hold for 2× rewind)"
            onPointerDown={beginHold}
            onPointerUp={() => endHold(-15)}
            onPointerLeave={cancelHold}
            onPointerCancel={cancelHold}
          >
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" />
            </svg>
            <span className="vs-fp-skip-num">15</span>
          </button>

          <button type="button" className="vs-fp-play-btn" data-testid="full-player-play-button" aria-label={player.isPlaying ? 'Pause' : 'Play'} onClick={togglePlay}>
            {player.isPlaying ? (
              <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></svg>
            ) : (
              <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor"><path d="M7 4v16l13-8z" /></svg>
            )}
          </button>

          <button
            type="button"
            className="vs-fp-skip-btn"
            data-testid="full-player-forward-15"
            aria-label="Forward 15 seconds (hold for 2× fast-forward)"
            onPointerDown={beginHold}
            onPointerUp={() => endHold(15)}
            onPointerLeave={cancelHold}
            onPointerCancel={cancelHold}
          >
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12a9 9 0 1 1-3-6.7L21 8" /><path d="M21 3v5h-5" />
            </svg>
            <span className="vs-fp-skip-num">15</span>
          </button>

          <button
            type="button"
            className="vs-fp-chapter-btn"
            data-testid="full-player-chapter-next"
            aria-label="Next chapter"
            disabled={chapters.length === 0 || (currentChapterIndex >= 0 && currentChapterIndex >= chapters.length - 1)}
            onClick={() => goChapter('next')}
          >
            <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
              <path d="M4 5l11 7L4 19z" /><path d="M18 5v14" />
            </svg>
          </button>
        </div>

        <button type="button" className="vs-fp-cta" data-testid="full-player-open-cta" onClick={openFullPlayer}>
          Open Full Player
        </button>
      </motion.div>

      {/* Sleep timer bottom sheet */}
      <AnimatePresence>
        {sleepSheetOpen && (
          <>
            <motion.div className="vs-fp-backdrop" data-testid="sleep-sheet-backdrop" style={{ zIndex: 9200 }}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
              onClick={() => setSleepSheetOpen(false)}
            />
            <motion.div className="vs-fp-sleep-sheet" data-testid="sleep-sheet"
              initial={{ y: 320 }} animate={{ y: 0 }} exit={{ y: 320 }}
              transition={{ type: 'spring', damping: 30, stiffness: 320 }}
            >
              <div className="vs-fp-handle" aria-hidden="true" />
              <div className="vs-fp-sleep-title">Sleep Timer</div>
              <div className="vs-fp-sleep-options">
                {SLEEP_OPTIONS.map((opt) => {
                  const active = (opt.minutes === 0 && !sleepEndAt)
                    || (opt.minutes > 0 && sleepEndAt && sleepRemainingMs > 0
                        && Math.abs((sleepEndAt - Date.now()) / 60000 - opt.minutes) < 0.5);
                  return (
                    <button key={opt.id} type="button" className={`vs-fp-sleep-opt ${active ? 'is-active' : ''}`}
                      data-testid={`sleep-option-${opt.id}`} onClick={() => setSleep(opt.minutes)}>
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Chapters bottom sheet */}
      <AnimatePresence>
        {chaptersSheetOpen && (
          <>
            <motion.div className="vs-fp-backdrop" data-testid="chapters-sheet-backdrop" style={{ zIndex: 9200 }}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
              onClick={() => setChaptersSheetOpen(false)}
            />
            <motion.div className="vs-fp-chapters-sheet" data-testid="chapters-sheet"
              initial={{ y: 600 }} animate={{ y: 0 }} exit={{ y: 600 }}
              transition={{ type: 'spring', damping: 30, stiffness: 320 }}
            >
              <div className="vs-fp-handle" aria-hidden="true" />
              <div className="vs-fp-chapters-header">
                <div className="vs-fp-sleep-title">Chapters</div>
                <button type="button" className="vs-fp-icon-btn" data-testid="chapters-sheet-close" aria-label="Close chapters list" onClick={() => setChaptersSheetOpen(false)}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 6 6 18" /><path d="m6 6 12 12" />
                  </svg>
                </button>
              </div>
              <div className="vs-fp-chapters-list">
                {chapters.length === 0 ? (
                  <div className="vs-fp-chapters-empty">No chapters available.</div>
                ) : chapters.map((c, i) => {
                  const isCurrent = i === currentChapterIndex;
                  const isPast = currentChapterIndex >= 0 && i < currentChapterIndex;
                  return (
                    <button
                      key={c.id || `ch-${c.chapter_number || i}`}
                      type="button"
                      className={`vs-fp-chapter-row ${isCurrent ? 'is-current' : ''} ${isPast ? 'is-past' : ''}`}
                      data-testid={`chapter-row-${c.chapter_number || i + 1}`}
                      onClick={() => jumpToChapter(c)}
                    >
                      <span className="vs-fp-chapter-dot" aria-hidden="true" />
                      <span className="vs-fp-chapter-row-num">{c.chapter_number || i + 1}</span>
                      <span className="vs-fp-chapter-row-title">{c.title || `Chapter ${c.chapter_number || i + 1}`}</span>
                      {isCurrent && (
                        <span className="vs-fp-chapter-row-now" aria-label="Now playing">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M3 5v14h2V5H3zm4 0v14l11-7L7 5z" />
                          </svg>
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default FullPlayerModal;
export { dispatchCommand, hapt };
